import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import ElectionsModule from 'store/modules/elections/electionsModule';
import EmployeeProfileTab from './employeeProfileTab';
import DependentsTab from './dependentsTab';
import { BaseFullEmployeeFormDomainParams, TabId } from './types';
import { isAllowedTabId, syncSubscriberIdToUrl } from './utils';

/**
 * This page has a problem of relying on electionModule. Actually this module
 * is only needed for elections feature which is not needed on all the pages that my have full form feature.
 * What we probably need is some subscriberModule interface that will be required in this page.
 * On pages where we need elections, we will pass election module in an adapter that will give the necessary API for
 * that feature. On other pages we will use some other modules with the same interface.
 */

export interface IFullFormPageStoreShape {
  readonly electionsModule: ElectionsModule;
  readonly employeeProfileTab: EmployeeProfileTab;
  readonly dependentsTab: DependentsTab;
  readonly subscriberExists: boolean;
  readonly isInitialDataLoading: boolean;
}

export default abstract class IFullFormPageStore<DomainParams extends BaseFullEmployeeFormDomainParams>
  implements IFullFormPageStoreShape
{
  protected _domainData: DomainParams;
  protected readonly _allowedTabIds: readonly TabId[];
  protected _currentTabId: TabId = TabId.EMPLOYEE_PROFILE;
  protected _isInitialDataLoading = true;
  protected _isLoading = false;
  protected _electionsModule: ElectionsModule;
  protected _employeeProfileTab: EmployeeProfileTab;
  protected _dependentsTab: DependentsTab;

  protected abstract createElectionsModule(): ElectionsModule;
  protected abstract _fetchCoreData(): Promise<void>;
  protected abstract getPrimaryFormId(): string | undefined;
  public abstract onBackRedirect(): void;

  protected getDependentFormId(): string | undefined {
    return this._electionsModule.requiredSubInfo?.manageDependentFormId;
  }

  public get allowedTabIds(): readonly TabId[] {
    return this._allowedTabIds;
  }

  protected onTabChange(tabId: TabId): void {
    if (tabId === TabId.ELECTIONS) {
      this._electionsModule.subscribers[0]?.syncElectionDefaults();
    }
  }

  public get domainData(): DomainParams {
    return this._domainData;
  }

  public get electionsModule(): ElectionsModule {
    return this._electionsModule;
  }

  public get currentTabId(): TabId {
    return this._currentTabId;
  }

  public get employeeProfileTab(): EmployeeProfileTab {
    return this._employeeProfileTab;
  }

  public get dependentsTab(): DependentsTab {
    return this._dependentsTab;
  }

  public get subscriberExists(): boolean {
    return this._electionsModule.subscribersExist;
  }

  public get aggregatedErrors(): string[] {
    return this._employeeProfileTab.errors;
  }

  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  protected _setIsInitialDataLoading(isLoading: boolean): void {
    this._isInitialDataLoading = isLoading;
  }

  protected _setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  private async _resetPage(): Promise<void> {
    this._currentTabId = TabId.EMPLOYEE_PROFILE;
    this._electionsModule.resetSubscribers();
    this._domainData.subscriberId = undefined;
    await this.employeeProfileTab.fetchForm();
  }

  public setCurrentTabId(tabId: string): void {
    if (!isAllowedTabId(tabId, this.allowedTabIds)) {
      throw new Error(`Invalid tab ID: ${tabId} . Allowed tabs are: ${this.allowedTabIds.join(', ')}`);
    }
    this.onTabChange(tabId);
    this._currentTabId = tabId;
  }

  public async getInitialData(): Promise<void> {
    this._setIsInitialDataLoading(true);
    await this._fetchCoreData();
    await runInAction(async () => {
      if (this._domainData.subscriberId) {
        await this._electionsModule.fetchSubscriberById(this._domainData.subscriberId);
      }
    });
    await runInAction(async () => {
      await this.employeeProfileTab.fetchForm();
    });
    runInAction(() => {
      this._setIsInitialDataLoading(false);
    });
  }

  public async updateSubscriberData(): Promise<void> {
    if (!this._domainData.subscriberId) return;
    this._setIsLoading(true);
    await this._electionsModule.fetchSubscriberById(this._domainData.subscriberId);
    runInAction(() => {
      this._setIsLoading(false);
    });
  }

  public async setupPageForNewEmployee(subscriberId: string): Promise<void> {
    this._electionsModule = this.createElectionsModule();
    this._setIsInitialDataLoading(true);
    await this._fetchCoreData();
    await runInAction(async () => {
      await this._electionsModule.fetchSubscriberById(subscriberId);
    });
    await runInAction(async () => {
      await this.employeeProfileTab.fetchForm();
    });

    runInAction(() => {
      this._domainData.subscriberId = subscriberId;
      syncSubscriberIdToUrl(subscriberId);
      this._setIsInitialDataLoading(false);
    });
  }

  public async onAddAnotherEmployee(): Promise<void> {
    await this._resetPage();
  }

  constructor(domainData: DomainParams, allowedTabIds: readonly TabId[]) {
    this._domainData = domainData;
    this._allowedTabIds = allowedTabIds;
    this._electionsModule = this.createElectionsModule();

    if (this._domainData.activeTabId) {
      if (!isAllowedTabId(this._domainData.activeTabId, this._allowedTabIds)) {
        throw new Error(
          `Invalid tab ID: ${this._domainData.activeTabId} . Allowed tabs are: ${this._allowedTabIds.join(', ')}`,
        );
      }

      this._currentTabId = this._domainData.activeTabId;
    }

    this._employeeProfileTab = new EmployeeProfileTab(this._domainData, this, () => this.getPrimaryFormId());
    this._dependentsTab = new DependentsTab(this, () => this.getDependentFormId());

    makeObservable<
      IFullFormPageStore<DomainParams>,
      | '_domainData'
      | '_currentTabId'
      | '_electionsModule'
      | '_isLoading'
      | '_resetPage'
      | '_isInitialDataLoading'
      | '_setIsInitialDataLoading'
      | '_setIsLoading'
    >(this, {
      _domainData: observable,
      _currentTabId: observable,
      _electionsModule: observable,
      _isInitialDataLoading: observable,
      _isLoading: observable,
      currentTabId: computed,
      domainData: computed,
      electionsModule: computed,
      subscriberExists: computed,
      aggregatedErrors: computed,
      isInitialDataLoading: computed,
      isLoading: computed,
      _setIsInitialDataLoading: action.bound,
      _setIsLoading: action.bound,
      _resetPage: action.bound,
      setCurrentTabId: action.bound,
      onAddAnotherEmployee: action.bound,
      getInitialData: action.bound,
      updateSubscriberData: action.bound,
      setupPageForNewEmployee: action.bound,
    });
  }
}
